import Typography from "@/shared/ui/Typography";
import * as React from "react";

import ProjectCard from "@/shared/ui/cards/ProjectCard";
import type { ProjectsType } from "@/entities/projects/model/types";
import SliderArrowButton from "@/shared/ui/buttons/SliderArrowButton";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, FreeMode } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";

import "swiper/css";
import "swiper/css/navigation";

export interface ICustomSliderSectionProps {
    projects: ProjectsType | undefined;
}

export function CustomSliderSection({ projects }: ICustomSliderSectionProps) {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const swiperRef = React.useRef<SwiperType | null>(null);

    const [winWidth, setWinWidth] = React.useState<number>(window.innerWidth);
    const [conWidth, setConWidth] = React.useState<number>(0);
    const [currentIndex, setCurrentIndex] = React.useState<number>(0);

    const [showPrev, setShowPrev] = React.useState(false);
    const [showNext, setShowNext] = React.useState(true);

    const MOBILE_BREAKPOINT = 768;

    React.useEffect(() => {
        const handleResize = () => {
            setWinWidth(window.innerWidth);
            setConWidth(containerRef.current?.offsetWidth ?? 0);
        };

        window.addEventListener("resize", handleResize);
        setConWidth(containerRef.current?.offsetWidth ?? 0);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const sliderPadding = Math.max((winWidth - conWidth) / 2, 0);
    const isMobile = winWidth < MOBILE_BREAKPOINT;

    const handleSlideChange = (swiper: SwiperType) => {
        setCurrentIndex(swiper.realIndex)
        setShowPrev(!swiper.isBeginning);
        setShowNext(!swiper.isEnd);
    };

    const handlePrevButton = () => {
        swiperRef.current?.slidePrev();
    };

    const handleNextButton = () => {
        swiperRef.current?.slideNext();
    };

    if (!projects) return null;

    return (
        <section className="py-[40px] md:py-[52px] flex flex-col gap-[20px] md:gap-[28px]">
            <div className="container">
                <Typography tag="h2" className="" ref={containerRef}>
                    Экспертные подборки обьектов
                </Typography>
            </div>

            <div className="max-w-full relative">
                {showPrev && (
                    <SliderArrowButton
                        className="absolute z-10 left-[10px] md:left-[32px] lg:left-[80px] top-1/2 -translate-y-1/2"
                        arrow="left"
                        onClick={handlePrevButton}
                    />
                )}

                <Swiper
                    modules={[Navigation, FreeMode]}
                    onSwiper={(swiper) => {
                        swiperRef.current = swiper;
                        handleSlideChange(swiper);
                    }}
                    onSlideChange={handleSlideChange}
                    spaceBetween={0}
                    slidesPerView="auto"
                    freeMode={!isMobile}
                    centeredSlides={isMobile}
                    slideToClickedSlide={isMobile}
                    slidesOffsetBefore={sliderPadding}
                    slidesOffsetAfter={sliderPadding}
                >
                    {projects.map((project, index) => (
                        <SwiperSlide
                            key={project.id}
                            className="max-w-[300px] md:max-w-[400px]"
                        >
                            <ProjectCard
                                className={
                                    index === 0
                                        ? "rounded-l-[10px]"
                                        : index === projects.length - 1
                                          ? "rounded-r-[10px]"
                                          : ""
                                }
                                project={project}
                                isActive={currentIndex == index && winWidth <= 768}
                            />
                        </SwiperSlide>
                    ))}
                </Swiper>

                {showNext && (
                    <SliderArrowButton
                        className="absolute z-10 right-[10px] md:right-[32px] lg:right-[80px] top-1/2 -translate-y-1/2"
                        arrow="right"
                        onClick={handleNextButton}
                    />
                )}
            </div>
        </section>
    );
}
